import { MemberPickerDialog } from '@/components/governance/MemberPickerDialog';
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Plus, Trash2, Info, Lock, Users, Eye, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useTranslation } from 'react-i18next';
import { useTalentMapAccess, useTalentMapGovernance, useTalentMapCommunityPicker, useTalentMapLeaders, useSaveTalentMapGovernance } from '@/hooks/usePerformance';

// ── Roles (espejo del backend lib/talent-map-access.ts) ──────────────────────
type TalentMapRole =
  | 'people_director'
  | 'compensation_lead'
  | 'development_partner'
  | 'hrbp_lead'
  | 'hrbp_sr_analyst'
  | 'c_level'
  | 'platform_admin';

const ROLE_OPTIONS: TalentMapRole[] = [
  'people_director',
  'compensation_lead',
  'development_partner',
  'hrbp_lead',
  'hrbp_sr_analyst',
  'c_level',
  'platform_admin',
];

const ROLE_LABELS: Record<TalentMapRole, string> = {
  people_director: 'Directora de People',
  compensation_lead: 'Lead de Compensaciones',
  development_partner: 'Development Partner',
  hrbp_lead: 'HRBP Lead',
  hrbp_sr_analyst: 'HRBP Sr. Analyst',
  // Euge 24/09: "lo mismo que HRBP Lead" — ve toda la base, edita categorías
  // (avisa al líder directo por Slack). No administra Permisos.
  c_level: 'C-Level',
  platform_admin: 'Admin de plataforma (solo lectura)',
};

/** Roles que además pueden EDITAR categorías (override). */
const EDIT_ROLES: TalentMapRole[] = ['hrbp_lead', 'hrbp_sr_analyst', 'c_level'];

interface PeopleRow {
  email: string;
  name?: string;
  role: TalentMapRole;
  active: boolean;
}

export function TalentMapGovernancePage() {
  const { t } = useTranslation();
  const { data: access, isLoading: accessLoading } = useTalentMapAccess();
  const canAdmin = access?.isGovernanceAdmin ?? false;

  const { data: governance, isLoading: govLoading, isError: govError } = useTalentMapGovernance();
  const { data: members = [], isLoading: membersLoading } = useTalentMapCommunityPicker();
  const { data: leaders = [], isLoading: leadersLoading } = useTalentMapLeaders();
  const save = useSaveTalentMapGovernance();

  // ── Estado local editable ──────────────────────────────────────────────────
  const [people, setPeople] = useState<PeopleRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRole, setPickerRole] = useState<TalentMapRole>('hrbp_lead');

  useEffect(() => {
    if (governance) {
      setPeople(
        (governance.people ?? []).map((p) => ({
          email: p.email.toLowerCase(),
          name: p.name,
          role: p.role as TalentMapRole,
          active: p.active,
        }))
      );
      setDirty(false);
    }
  }, [governance]);

  // email → miembro del community map (para mostrar nombre / rol / área)
  const memberByEmail = useMemo(() => {
    const map = new Map<string, (typeof members)[number]>();
    for (const m of members) map.set(m.email.toLowerCase(), m);
    return map;
  }, [members]);

  const rosterEmails = useMemo(() => new Set(people.map((p) => p.email)), [people]);


  // ── Handlers ────────────────────────────────────────────────────────────────
  const addPerson = (email: string, name?: string) => {
    setPeople((prev) => [...prev, { email: email.toLowerCase(), name, role: pickerRole, active: true }]);
    setDirty(true);
  };
  const removePerson = (email: string) => {
    setPeople((prev) => prev.filter((p) => p.email !== email));
    setDirty(true);
  };
  const changeRole = (email: string, role: TalentMapRole) => {
    setPeople((prev) => prev.map((p) => (p.email === email ? { ...p, role } : p)));
    setDirty(true);
  };
  const toggleActive = (email: string, active: boolean) => {
    setPeople((prev) => prev.map((p) => (p.email === email ? { ...p, active } : p)));
    setDirty(true);
  };
  const handleSave = () => {
    save.mutate(
      { people: people.map((p) => ({ email: p.email, name: p.name, role: p.role, active: p.active })) },
      { onSuccess: () => setDirty(false) }
    );
  };

  // ── Estados de carga / acceso ────────────────────────────────────────────────
  if (accessLoading || govLoading) {
    return <div className="flex items-center justify-center py-24"><WinclapLoader /></div>;
  }

  if (!canAdmin || govError) {
    return (
      <div className="py-16 text-center space-y-4">
        <Shield className="h-10 w-10 mx-auto text-muted-foreground/40" />
        <p className="text-muted-foreground">{t('performance.talentmapgovernance.no_tenes_acceso_al_panel_de')}</p>
        <Button variant="ghost" asChild>
          <Link to="/performance/admin">{t('performance.talentmapgovernance.volver_a_admin')}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-brand-green" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('performance.talentmapgovernance.permisos_del_mapa_de_talento')}</h1>
            <p className="text-sm text-muted-foreground">{t('performance.talentmapgovernance.quien_ve_la_base_completa_y')}</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={!dirty || save.isPending}>
          {save.isPending ? t('performance.talentmapgovernance.guardando') : t('performance.talentmapgovernance.guardar_cambios')}
        </Button>
      </div>

      {/* Matriz de permisos (POV por rol) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('performance.talentmapgovernance.como_cambia_la_vista_segun_el')}</CardTitle>
          <CardDescription>
            {t('performance.talentmapgovernance.el_acceso_al_mapa_de_talento')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rol</TableHead>
                <TableHead className="text-center">Ve</TableHead>
                <TableHead className="text-center">{t('performance.talentmapgovernance.edita_categorias')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium flex items-center gap-2"><Users className="h-4 w-4 text-muted-foreground" /> {t('performance.talentmapgovernance.lider_auto')}</TableCell>
                <TableCell className="text-center text-sm text-muted-foreground">{t('performance.talentmapgovernance.solo_sus_reportes_directos_historial')}</TableCell>
                <TableCell className="text-center text-muted-foreground">{t('performance.talentmapgovernance.propone_y_actualiza')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium flex items-center gap-2"><Eye className="h-4 w-4 text-muted-foreground" /> {t('performance.talentmapgovernance.people_director_comp_dev')}</TableCell>
                <TableCell className="text-center text-sm text-muted-foreground">{t('performance.talentmapgovernance.base_completa_export')}</TableCell>
                <TableCell className="text-center text-muted-foreground">No</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium flex items-center gap-2"><Pencil className="h-4 w-4 text-muted-foreground" /> {t('performance.talentmapgovernance.hrbp_lead_sr_analyst')}</TableCell>
                <TableCell className="text-center text-sm text-muted-foreground">{t('performance.talentmapgovernance.base_completa_export')}</TableCell>
                <TableCell className="text-center text-emerald-600 font-medium">{t('performance.talentmapgovernance.si_override')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium flex items-center gap-2"><Pencil className="h-4 w-4 text-muted-foreground" /> {t('performance.talentmapgovernance.c_level_full')}</TableCell>
                <TableCell className="text-center text-sm text-muted-foreground">{t('performance.talentmapgovernance.base_completa_export')}</TableCell>
                <TableCell className="text-center text-emerald-600 font-medium">{t('performance.talentmapgovernance.si_override_c_level')}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Roster de People */}
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg">{t('performance.talentmapgovernance.roster_de_people')}</CardTitle>
            <CardDescription>
              {t('performance.talentmapgovernance.personas_con_acceso_a_la_base')}
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={() => setPickerOpen(true)}>
            <Plus className="h-3.5 w-3.5" /> {t('performance.talentmapgovernance.agregar_persona')}
          </Button>
        </CardHeader>
        <CardContent>
          {people.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              {t('performance.talentmapgovernance.sin_personas_en_el_roster_solo')}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="w-[210px]">Rol</TableHead>
                  <TableHead className="w-[90px] text-center">Activo</TableHead>
                  <TableHead className="w-[48px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {people.map((p) => {
                  const m = memberByEmail.get(p.email);
                  const inactive = m && m.status && m.status !== 'Activo';
                  return (
                    <TableRow key={p.email} className={p.active ? '' : 'opacity-60'}>
                      <TableCell className="font-medium">
                        {m?.name || p.name || p.email}
                        {!m && <span className="ml-2 text-xs text-amber-600">{t('performance.talentmapgovernance.no_esta_en_el_community_map')}</span>}
                        {inactive && <span className="ml-2 text-xs text-amber-600">({m!.status})</span>}
                        {EDIT_ROLES.includes(p.role) && (
                          <Badge variant="secondary" className="ml-2 text-[10px] gap-1"><Pencil className="h-2.5 w-2.5" /> edita</Badge>
                        )}
                        <div className="text-xs text-muted-foreground">{p.email}</div>
                      </TableCell>
                      <TableCell>
                        <Select value={p.role} onValueChange={(v) => changeRole(p.email, v as TalentMapRole)}>
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLE_OPTIONS.map((r) => (
                              <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch checked={p.active} onCheckedChange={(v) => toggleActive(p.email, v)} />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => removePerson(p.email)}
                          aria-label={t('performance.talentmapgovernance.quitar_persona')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Líderes resueltos (read-only) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Lock className="h-4 w-4 text-muted-foreground" /> {t('performance.talentmapgovernance.lideres_automatico')}
          </CardTitle>
          <CardDescription>
            {t('performance.talentmapgovernance.resueltos_en_vivo_desde_el_community')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {leadersLoading ? (
            <div className="flex items-center justify-center py-6"><WinclapLoader /></div>
          ) : leaders.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{t('performance.talentmapgovernance.sin_lideres_detectados_en_el_community')}</p>
          ) : (
            <ScrollArea className="max-h-64">
              <div className="flex flex-wrap gap-2">
                {leaders.map((l) => (
                  <Badge key={l.key} variant="secondary" className="gap-1.5 py-1 text-sm font-normal">
                    <span className="font-medium">{l.name}</span>
                    <span className="text-muted-foreground">· {l.reportsCount} {l.reportsCount === 1 ? 'reporte' : 'reportes'}</span>
                  </Badge>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        {t('performance.talentmapgovernance.los_admins_de_la_plataforma_siempre')}
      </p>

      {/* Picker de miembros */}
      <MemberPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title={t('performance.talentmapgovernance.agregar_persona_al_roster')}
        description={t('performance.talentmapgovernance.elegi_el_rol_y_hace_clic')}
        searchPlaceholder={t('performance.talentmapgovernance.buscar_por_nombre_mail_rol_o')}
        noResultsText={t('performance.talentmapgovernance.sin_resultados')}
        members={members}
        loading={membersLoading}
        exclude={rosterEmails}
        onPick={(m) => addPerson(m.email, m.name)}
      >
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">Rol:</span>
              <Select value={pickerRole} onValueChange={(v) => setPickerRole(v as TalentMapRole)}>
                <SelectTrigger className="h-9 w-[220px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
        </MemberPickerDialog>
    </div>
  );
}
